import { useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useFetchPilotCli } from '../api';
import { cliStatusLabel, type CliRow } from '../types';

export function PilotCliListPage() {
  const location = useLocation();
  const isApiRoute = location.pathname === '/cli-api-pilot-list';
  const search = useFetchPilotCli();
  const [pilotNumber, setPilotNumber] = useState<string>('');
  const [searched, setSearched] = useState<boolean>(false);

  const rows = search.data ?? [];

  const columns = useMemo<ColumnDef<CliRow>[]>(
    () => [
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.cliId}
          </span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
      {
        accessorKey: 'cliStatus',
        header: 'Status',
        size: 120,
        cell: ({ row }) => cliStatusLabel(row.original.cliStatus),
      },
      { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 140 },
    ],
    [],
  );

  const canViewPilots = usePermission(
    isApiRoute ? 'Manage API CLI.Api Pilot no. CLI' : 'Manage CLI.Pilot no. CLI',
    BASE_PERMISSION.VIEW,
  );

  if (!canViewPilots) return <Navigate to={isApiRoute ? '/cli-api-list' : '/cli-list'} replace />;

  const handleSearch = () => {
    const trimmed = pilotNumber.trim();
    if (!trimmed) {
      toast.error('Enter a pilot number to search.');
      return;
    }
    setSearched(true);
    search.mutate(trimmed, {
      onError: () => toast.error('Could not fetch pilot CLIs.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="Pilots"
        description="Find every CLI attached to a given pilot number."
        actions={
          <Button asChild variant="outline">
            <Link to={isApiRoute ? '/cli-api-list' : '/cli-list'}>
              <ArrowLeft className="h-4 w-4" />
              Back to CLIs
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Search by pilot number</CardTitle>
          <CardDescription>
            Enter the pilot number whose CLIs you want to inspect.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="pilot-input">Pilot number</Label>
              <Input
                id="pilot-input"
                value={pilotNumber}
                onChange={(e) => setPilotNumber(e.target.value)}
                placeholder="e.g. 91140000000"
              />
            </div>
            <Button type="submit" disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      <DataTable<CliRow, unknown>
        tableId="cli:pilot:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={searched ? 'No CLIs found' : 'Search to begin'}
        emptyDescription={
          searched
            ? 'No CLIs are attached to that pilot number.'
            : 'Enter a pilot number above and click Search.'
        }
        getRowId={(row) => String(row.cliId)}
        exportFilename="pilot-cli.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
